import fs from 'fs';
import path from 'path';
import os from 'os';
import { logger } from '../utils/logger';
import { DSLRendererProps, DSLRendererPropsSchema } from './types';

/**
 * ReportManager class to handle CRUD operations on reports
 * All operations read/write directly to files (no in-memory caching)
 */
export class ReportManager {
  private reportsBasePath: string;
  private projectId: string;

  /**
   * Initialize ReportManager with project ID
   * @param projectId - Project ID to use in file path
   */
  constructor(projectId: string = 'snowflake-dataset') {
    this.projectId = projectId;
    this.reportsBasePath = path.join(
      os.homedir(),
      '.superatom',
      'projects',
      projectId,
      'reports'
    );
  }

  /**
   * Get the file path for a specific report
   * @param reportId - Report ID
   * @returns Full path to report data.json file
   */
  private getReportPath(reportId: string): string {
    return path.join(this.reportsBasePath, reportId, 'data.json');
  }

  /**
   * Create a new report
   * @param reportId - Unique report ID
   * @param report - Report data
   * @returns Created report with metadata
   */
  createReport(reportId: string, report: DSLRendererProps): DSLRendererProps {
    const reportPath = this.getReportPath(reportId);
    const reportDir = path.dirname(reportPath);

    // Check if report already exists
    if (fs.existsSync(reportPath)) {
      throw new Error(`Report '${reportId}' already exists`);
    }

    // Validate report structure
    const validated = DSLRendererPropsSchema.parse(report);

    // Create directory structure
    fs.mkdirSync(reportDir, { recursive: true });

    // Write report to file
    fs.writeFileSync(reportPath, JSON.stringify(validated, null, 4));

    logger.info(`Report created: ${reportId}`);
    return validated;
  }

  /**
   * Get a specific report by ID
   * @param reportId - Report ID
   * @returns Report data or null if not found
   */
  getReport(reportId: string): DSLRendererProps | null {
    const reportPath = this.getReportPath(reportId);

    if (!fs.existsSync(reportPath)) {
      logger.warn(`Report not found: ${reportId}`);
      return null;
    }

    try {
      const fileContent = fs.readFileSync(reportPath, 'utf-8');
      const report = JSON.parse(fileContent) as DSLRendererProps;

      // Validate structure
      const validated = DSLRendererPropsSchema.parse(report);
      return validated;
    } catch (error) {
      logger.error(`Failed to read report ${reportId}:`, error);
      return null;
    }
  }

  /**
   * Get all reports
   * @returns Array of report objects with their IDs
   */
  getAllReports(): Array<{ reportId: string; report: DSLRendererProps }> {
    // Create base directory if it doesn't exist
    if (!fs.existsSync(this.reportsBasePath)) {
      fs.mkdirSync(this.reportsBasePath, { recursive: true });
      return [];
    }

    const reports: Array<{ reportId: string; report: DSLRendererProps }> = [];

    try {
      const reportDirs = fs.readdirSync(this.reportsBasePath);

      for (const reportId of reportDirs) {
        const reportPath = this.getReportPath(reportId);

        if (fs.existsSync(reportPath)) {
          const report = this.getReport(reportId);
          if (report) {
            reports.push({ reportId, report });
          }
        }
      }

      logger.debug(`Retrieved ${reports.length} reports`);
      return reports;
    } catch (error) {
      logger.error('Failed to get all reports:', error);
      return [];
    }
  }

  /**
   * Update an existing report
   * @param reportId - Report ID
   * @param report - Updated report data
   * @returns Updated report or null if not found
   */
  updateReport(reportId: string, report: DSLRendererProps): DSLRendererProps | null {
    const reportPath = this.getReportPath(reportId);

    if (!fs.existsSync(reportPath)) {
      logger.warn(`Report not found for update: ${reportId}`);
      return null;
    }

    try {
      // Validate report structure
      const validated = DSLRendererPropsSchema.parse(report);

      // Write updated report to file
      fs.writeFileSync(reportPath, JSON.stringify(validated, null, 4));

      logger.info(`Report updated: ${reportId}`);
      return validated;
    } catch (error) {
      logger.error(`Failed to update report ${reportId}:`, error);
      throw error;
    }
  }

  /**
   * Delete a report
   * @param reportId - Report ID
   * @returns True if deleted, false if not found
   */
  deleteReport(reportId: string): boolean {
    const reportPath = this.getReportPath(reportId);
    const reportDir = path.dirname(reportPath);

    if (!fs.existsSync(reportPath)) {
      logger.warn(`Report not found for deletion: ${reportId}`);
      return false;
    }

    try {
      // Delete the entire report directory
      fs.rmSync(reportDir, { recursive: true, force: true });

      logger.info(`Report deleted: ${reportId}`);
      return true;
    } catch (error) {
      logger.error(`Failed to delete report ${reportId}:`, error);
      return false;
    }
  }

  /**
   * Check if a report exists
   * @param reportId - Report ID
   * @returns True if report exists, false otherwise
   */
  reportExists(reportId: string): boolean {
    const reportPath = this.getReportPath(reportId);
    return fs.existsSync(reportPath);
  }

  /**
   * Get report count
   * @returns Number of reports
   */
  getReportCount(): number {
    if (!fs.existsSync(this.reportsBasePath)) {
      return 0;
    }

    try {
      const reportDirs = fs.readdirSync(this.reportsBasePath);
      return reportDirs.filter((dir) => {
        const reportPath = this.getReportPath(dir);
        return fs.existsSync(reportPath);
      }).length;
    } catch (error) {
      logger.error('Failed to get report count:', error);
      return 0;
    }
  }
}
